import React, { useEffect, useState, useContext } from 'react';
import './Sidebar.css';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { RefreshContext } from '../../../App';
import {
    IoChevronBack, IoChevronForward, IoGridOutline, IoSettingsOutline, IoLogOutOutline,
    IoChevronDown, IoPeopleOutline, IoPersonAddOutline, IoCalendarOutline,
    IoCheckmarkDoneCircleOutline, IoWalkOutline, IoWalletOutline
} from 'react-icons/io5';
import BrandLogo from '../../../shared-ui/BrandLogo';
import platformLogo from '../../../assets/branding/cranesoftwares-logo.png';
import '../../../shared-ui/crane-tokens.css';

const API_BASE_URL = process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL;
const ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR = process.env.REACT_APP_ZTOOL_URL;
const GET_COMPANY_DETAILS_ENDPOINT = `${API_BASE_URL}/api/get-company`;
const GET_USERS_ENDPOINT = `${API_BASE_URL}/api/get-users`;
const AUTH_TOKEN_KEY = 'zTaskAuthToken';
const USER_DETAILS_KEY = 'zTaskUserDetails';

const Sidebar = ({ refreshKey }) => {
    const location = useLocation();
    const { triggerRefresh } = useContext(RefreshContext);

    const [isCollapsed, setIsCollapsed] = useState(true);
    const [userDetails, setUserDetails] = useState(null);
    const [currentUserData, setCurrentUserData] = useState(null);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [companyName, setCompanyName] = useState('My App');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const stored = localStorage.getItem(USER_DETAILS_KEY);
        if (stored) {
            try {
                setUserDetails(JSON.parse(stored));
            } catch (e) {
                console.error("Sidebar: Error parsing user details", e);
            }
        }
    }, [refreshKey]);

    useEffect(() => {
        const fetchUserData = async () => {
            const token = localStorage.getItem(AUTH_TOKEN_KEY);
            if (!token || !userDetails?.email) return;

            try {
                setIsLoading(true);
                // Fetch company details
                const companyRes = await fetch(GET_COMPANY_DETAILS_ENDPOINT, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (companyRes.ok) {
                    const companyData = await companyRes.json();
                    const companyList = companyData.message || companyData.messgae;
                    if (Array.isArray(companyList) && companyList.length > 0) {
                        setCompanyName(companyList[0].companyName || 'My App');
                    }
                }

                // Fetch user specific data (Role, Dept, etc)
                const usersRes = await fetch(GET_USERS_ENDPOINT, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (usersRes.ok) {
                    const usersData = await usersRes.json();
                    if (Array.isArray(usersData.userData)) {
                        const matchedUser = usersData.userData.find(u => u.email === userDetails.email);
                        if (matchedUser) {
                            setCurrentUserData({
                                role: matchedUser.role?.name || userDetails.role?.name || 'USER',
                                department: matchedUser.department?.departmentName || 'N/A',
                                designation: matchedUser.designation?.designationName || 'N/A'
                            });
                        } else {
                            // Fallback from userDetails if match fails
                            setCurrentUserData({
                                role: userDetails.role?.name || 'USER',
                                department: 'N/A',
                                designation: 'N/A'
                            });
                        }
                    }
                }
            } catch (error) {
                console.error("Sidebar: Error fetching user data", error);
                // Fallback to minimal data
                setCurrentUserData({
                    role: userDetails.role?.name || 'USER',
                    department: 'N/A',
                    designation: 'N/A'
                });
            } finally {
                setIsLoading(false);
            }
        };

        if (userDetails) {
            fetchUserData();
        }
    }, [userDetails]);

    const toggleSidebarCollapse = () => {
        setIsCollapsed(!isCollapsed);
        if (!isCollapsed) setIsProfileOpen(false);
    };

    const toggleProfileDropdown = (e) => {
        e.stopPropagation();
        if (!isCollapsed) setIsProfileOpen(!isProfileOpen);
    };

    const handleLogout = () => {
        localStorage.clear();
        if (ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR) {
            window.location.replace(ZTOOL_LOGIN_REDIRECT_URL_SIDEBAR);
        } else {
            window.location.href = '/login';
        }
    };

    const avatarInitial = userDetails?.name ? userDetails.name.charAt(0).toUpperCase() : 'U';

    const menuItems = [{ id: 'dashboard', icon: <IoGridOutline />, text: 'Dashboard', path: '/dashboard' }];

    // Role detection for menu filtering
    const userRole = (currentUserData?.role || userDetails?.role?.name || 'EMPLOYEE').toUpperCase();
    const isManagement = ['SUPER_ADMIN', 'ADMIN', 'HR'].includes(userRole);

    const hrmsItems = [
        { id: 'onboarding', icon: <IoPersonAddOutline />, text: 'Onboarding', path: '/onboarding' },
        { id: 'offboarding-exit', icon: <IoLogOutOutline />, text: 'Offboarding', path: '/offboarding-exit' },
        { id: 'holidays', icon: <IoCalendarOutline />, text: 'Holidays', path: '/holidays' },
        { id: 'attendance', icon: <IoCheckmarkDoneCircleOutline />, text: 'Attendance', path: '/attendance' },
        { id: 'leaves', icon: <IoWalkOutline />, text: 'Leaves Management', path: '/leaves' },
        { id: 'payroll', icon: <IoWalletOutline />, text: 'Payroll', path: '/payroll' },
    ];

    // Filter HRMS items based on role
    const filteredHrmsItems = hrmsItems;

    return (
        <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
            <div className="sidebar-header">
                <Link to="/" className="logo-link" aria-label="CraneSoftwares home">
                    <div className="logo">
                        {isCollapsed ? (
                            <BrandLogo src={platformLogo} collapsed size="sm" alt="CraneSoftwares" />
                        ) : (
                            <BrandLogo src={platformLogo} size="md" alt="CraneSoftwares" />
                        )}
                        {!isCollapsed && companyName && companyName !== 'My App' && (
                            <span className="logo-name-text" style={{ marginLeft: 8 }}>{companyName}</span>
                        )}
                    </div>
                </Link>
                <div onClick={toggleSidebarCollapse} className="collapse-icon-wrapper">
                    {isCollapsed ? <IoChevronForward className="collapse-icon" /> : <IoChevronBack className="collapse-icon" />}
                </div>
            </div>

            <div className="sidebar-content">
                <div className="menu-group">
                    {!isCollapsed && <p className="menu-group-title">MAIN</p>}
                    <ul>{menuItems.map(item => (
                        <li key={item.id}>
                            <NavLink to={item.path} className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}>
                                <span className="menu-item-icon">{item.icon}</span>
                                {!isCollapsed && <span className="menu-item-text">{item.text}</span>}
                            </NavLink>
                        </li>
                    ))}</ul>
                </div>

                <div className="menu-group">
                    {!isCollapsed && <p className="menu-group-title">HRMS</p>}
                    <ul>{filteredHrmsItems.map(item => (
                        <li key={item.id}>
                            <NavLink to={item.path} className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}>
                                <span className="menu-item-icon">{item.icon}</span>
                                {!isCollapsed && <span className="menu-item-text">{item.text}</span>}
                            </NavLink>
                        </li>
                    ))}</ul>
                </div>
            </div>

            <div className="sidebar-footer">
                <div className="user-profile-section" onClick={toggleProfileDropdown}>
                    <div className="user-profile-header-row">
                        <div className="avatar">{avatarInitial}</div>
                        {!isCollapsed && (
                            <>
                                <div className="user-header-info">
                                    <span className="user-name">{userDetails?.name || 'User'}</span>
                                    <span className="view-profile-text">View Profile</span>
                                </div>
                                <IoChevronDown className={`profile-dropdown-icon ${isProfileOpen ? 'open' : ''}`} />
                            </>
                        )}
                    </div>

                    {!isCollapsed && (
                        <div className={`user-details-container ${isProfileOpen ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
                            <div className="info-item">
                                <span className="info-label">Role</span>
                                <span className="info-value">{isLoading ? '...' : (currentUserData?.role || 'N/A')}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Dept</span>
                                <span className="info-value">{isLoading ? '...' : (currentUserData?.department || 'N/A')}</span>
                            </div>
                            <Link to="/settings/profile" className="full-settings-link">Go to full settings</Link>
                        </div>
                    )}
                </div>

                <div onClick={handleLogout} className="logout-section">
                    <span className="menu-item-icon"><IoLogOutOutline /></span>
                    {!isCollapsed && <span>Logout</span>}
                </div>
            </div>
        </div>
    );
};

export default Sidebar;